import { applyParams } from './applyParams.ts';
import { contentTypeMatches } from './contentTypeMatches.ts';
import { toQueryString } from '../query/toQueryString.ts';
import type { RouteParams, RouteQuery } from '../router/Router.ts';

const types = ['json', 'blob', 'text', 'arrayBuffer', 'bytes', 'unknown'] as const;

/** The values are the method on Response used to get the data. */
export type RadHttpRequestType = typeof types[number];

export type RadHttpResponseHandler<T> = (response: RadHttpResponse<T>) => void;

export interface RadHttpOptions<T = never> {
  /**
   * Key/value pairs for headers to send with the request.
   * Do not use with {@link rawHeaders}.
   */
  headers?: Record<string, string>;

  /**
   * Key/value pairs for headers or a Headers object.
   * If provided, the headers are not modified and Content-Type is not set automatically.
   * Do not use with {@link headers}.
   */
  rawHeaders?: HeadersInit;

  /**
   * The body to pass along with the request.
   * If it is an object, it will be JSON stringified.
   *
   * Use rawBody if you want to avoid stringification. Do not use with {@link rawBody}.
   */
  body?: string | object;

  /** The raw body to be passed along exactly as is. Do not use with {@link body}. */
  rawBody?: BodyInit;

  /** Key/value pairs to make up the query string. */
  query?: RouteQuery;

  params?: RouteParams;

  /**
   * The expected type of the response.
   * If the expected type does not line up with the resulting type, an error is thrown.
   * If {@link raw} is true, this is ignored.
   */
  type?: RadHttpRequestType;

  /**
   * If true, type is ignored and the data won't be parsed and {@link RadHttpResponse.data} will be undefined.
   */
  raw?: boolean;

  /** Additional options to be passed to fetch directly. */
  fetchOptions?: Partial<Request>;

  /**
   * If true and another request comes in with the same debounce name, previous
   *   calls will complete with no response and debounced = true
   */
  debounce?: boolean;

  /** Defaults to the url of the request, without parameters substituted. */
  debounceName?: string;

  /** A method that is called before the response is returned. */
  responseHandler?: RadHttpResponseHandler<T>;

  /** If true, the URL will have a cache-busting string appended. */
  force?: boolean;
}

export interface RadHttpResponse<T = void> {
  /** The actual URL given to fetch. */
  url: string;

  /** The actual options given to fetch. */
  options: RequestInit;

  /** The response from fetch. */
  response?: Response;

  /**
   * The parsed data, parsed based on the type,
   * using .json(), .blob(), .text(), .arrayBuffer(), or .bytes()
   *
   * If the request was made with {@link RadHttpOptions.raw} = true, this will be undefined and you should use
   * the appropriate method from {@link response} yourself.
   *
   * Unless undefined if {@link debounced}.
   */
  data?: T;

  /**
   * The type of response. It is based on the Content-Type and matches the following:
   *  - application/json => json
   *  - text/* => text
   *  - anything else => blob
   * If {@link RadHttpOptions.type} was specified, it is always this value.
   * If {@link RadHttpOptions.raw} was true and type was not specified, this will be undefined.
   */
  type?: RadHttpRequestType;

  /**
   * The error produced while attempting the request, or returned by the server.
   * Only possible from the server if type is JSON.
   * The error can technically be any type, but is probably an Error.
   */
  error?: Error | unknown;

  /** Indicates if the request was aborted. If true, data will always be undefined. */
  debounced?: boolean;

  /** Indicates if the request was successful. */
  ok?: boolean;

  /** Same as response.status. undefined if response is unset. */
  status?: number;

  verb: string;
}

export type RadHttpOkResponse<T = void> = Omit<RadHttpResponse<T>, 'data' | 'ok'> & { data: T, ok: true };

export function isOkResponse<T>(response?: RadHttpResponse<T>): response is RadHttpOkResponse<T> {
  return Boolean(response?.ok);
}

/** Error with the related response. */
export class RadHttpError extends Error {
  response?: Partial<RadHttpResponse>;

  constructor(response: Partial<RadHttpResponse>, message?: string) {
    super(message ?? (response?.error as Error)?.message ?? response?.error);

    this.response = response;
  }
}

export class RadHttp {
  /** Method to get default parameters. */
  getDefaultParam: (key: string) => string | number | undefined = () => '';

  /** If true, it will not console.error() errors */
  muted: boolean = false;

  /** The default response handler used when {@link RadHttpOptions.responseHandler} is not provided. */
  defaultResponseHandler: RadHttpResponseHandler<unknown> = () => {};

  /** A dictionary of debounceNames and their AbortController. */
  private debounceControllers: Record<string, AbortController> = {};

  /**
   * Performs an HTTP request.
   *
   * Note: RadHttp.type defaults to 'json'. If you want a different type, you'll have to manually specify.
   * @param verb
   * @param url The URL, which can contain param keys wrapped in curly brackets.
   *   E.g., /patient/{patientId}
   * @param options
   */
  async request<T = void>(verb: string, url: string, options: RadHttpOptions<T> = {}): Promise<RadHttpResponse<T>> {
    try {
      this.validateVerb(verb);
      this.validateUrl(url);
      this.validateOptions(options);
    } catch (ex) {
      return this.throwError(ex);
    }

    let { debounceName, type = 'json' } = options;
    const { body, rawBody, headers = {}, rawHeaders, query = {}, params = {}, fetchOptions = {}, debounce, responseHandler, force
    } = options;

    let response: Response | undefined = undefined;
    let debounced = false;
    let data: T | undefined = undefined;
    let error: unknown;

    try {
      const parsedUrl = this.parseUrl(url, { params, force, query });

      const requestOptions: RequestInit = {
        headers: rawHeaders ?? { 'Content-Type': 'application/json', ...headers },
        body: rawBody ?? (typeof body === 'object' ? JSON.stringify(body) : body),
        method: verb,
        ...fetchOptions
      };

      if (debounce) {
        debounceName ??= url;

        // Debounce the existing request.
        if (this.debounceControllers[debounceName]) {
          this.debounceControllers[debounceName].abort('debounce');
        }

        const abortController = new AbortController();
        requestOptions.signal = abortController.signal;

        this.debounceControllers[debounceName] = abortController;
      }

      try {
        response = await fetch(parsedUrl, requestOptions);

        ({ data, type, error } = await this.parseContent<T>(verb, url, options, requestOptions, response));
      } catch (ex) {
        if (typeof ex === 'string' && ex === 'debounce') {
          debounced = true;
        } else {
          return this.throwError(ex);
        }
      }

      if (debounce) {
        delete this.debounceControllers[debounceName!];
      }

      const result: RadHttpResponse<T> = {
        url: parsedUrl,
        options: requestOptions,
        response,
        type,
        data,
        error,
        debounced,
        ok: response?.ok ?? debounced,
        status: response?.status,
        verb
      };

      (responseHandler ?? this.defaultResponseHandler)?.(result);

      return result;
    } catch (err) {
      return this.throwError(err);
    }
  }

  async get<T = void>(url: string, options: RadHttpOptions<T> = {}): Promise<RadHttpResponse<T>> {
    return this.request<T>('GET', url, options);
  }

  async put<T = void>(url: string, options: RadHttpOptions<T> = {}): Promise<RadHttpResponse<T>> {
    return this.request<T>('PUT', url, options);
  }

  async post<T = void>(url: string, options: RadHttpOptions<T> = {}): Promise<RadHttpResponse<T>> {
    return this.request<T>('POST', url, options);
  }

  async delete(url: string, options: RadHttpOptions = {}): Promise<RadHttpResponse> {
    return this.request('DELETE', url, options);
  }

  async patch<T = void>(url: string, options: RadHttpOptions<T> = {}): Promise<RadHttpResponse<T>> {
    return this.request<T>('PATCH', url, options);
  }

  async head(url: string, options: RadHttpOptions = {}): Promise<RadHttpResponse> {
    return this.request('HEAD', url, options);
  }

  /** Parses and returns the URL. */
  parseUrl<T = void>(url: string, { params = {}, force = false, query = {} }: RadHttpOptions<T> = {}): string {
    if (force) {
      query[`${Date.now()}${Math.floor(Math.random() * Math.pow(10, 10))}`] = true;
    }

    try {
      return `${applyParams(url, params, this.getDefaultParam)}${toQueryString(query)}`;
    } catch (ex) {
      throw new Error(`${(ex as Error)?.message ?? ex}, URL: ${url}`);
    }
  }

  private async parseContent<T>(verb: string, url: string, { raw, type }: RadHttpOptions<T>, options: RequestInit, response: Response):
    Promise<{ data?: T, error?: unknown, type: RadHttpRequestType }> {
    const [contentType] = response.headers.get('Content-Type')?.split(';') || [];
    let data: T | undefined = undefined;
    let error: unknown;

    if (!raw && verb !== 'HEAD' && response.status !== 204 && response.headers.get('Content-Length') !== '0') {
      try {
        if (['application/json', 'application/problem+json'].includes(contentType) && (!type || type === 'json')) {
          const result = await response.json();
          type = 'json';

          data = result?.data ?? result;
          error = result?.error;
        } else if (type === 'text' || (!type && contentTypeMatches(contentType, 'text/*'))) {
          data = await response.text() as T;
          type = 'text';
        } else if (type === 'arrayBuffer') {
          data = await response.arrayBuffer() as T;
          type = 'arrayBuffer';
        } else if (type === 'bytes') {
          data = await response.bytes() as T;
          type = 'bytes';
        } else if (!type || type === 'blob') {
          data = await response.blob() as T;
          type = 'blob';
        } else if (type === 'unknown') {
          // we don't set the data, you must process it yourself
          data = undefined;
          type = 'unknown';
        }
      } catch (error) {
        return this.throwError(new RadHttpError({ verb, response, url, options, error }));
      }

      if (type !== 'unknown'
        && (data === undefined || data === null)
        && data !== ''
        && (error === undefined || error === null) && (error as unknown as string) !== ''
      ) {
        this.throwError(RadHttp.buildTypeMismatchError(type, contentType, response, url, options));
      }
    }

    type ??= 'unknown';

    return { data, error, type };
  }

  private throwError<T>(error: unknown): T {
    if (!this.muted) {
      console.warn(error);
    }

    throw error;
  }

  private validateVerb(verb: string) {
    if (!verb) {
      throw new Error('Must provide an HTTP verb. Unless you need a custom verb, '
        + 'it is recommended to use the HTTP method functions (get(), put(), post(), delete(), patch()).');
    }
  }

  private validateUrl(url: string) {
    if (!url) {
      throw new Error('Must provide a URL.');
    }
  }

  private validateOptions<T>({ body, rawBody, rawHeaders, headers, type }: RadHttpOptions<T>) {
    if ((body !== undefined) && (rawBody !== undefined)) {
      throw new Error('Cannot use "body" and "rawBody" in the same request.');
    }

    if (rawHeaders !== undefined && Object.keys(headers ?? {}).length) {
      throw new Error('Cannot specify headers on "headers" when using "rawHeaders" in the same request.');
    }

    if (type && !types.includes(type)) {
      throw new Error(`Unknown type: ${type}, expected one of: ${types.join(', ')}`);
    }
  }

  private static buildTypeMismatchError(type: RadHttpRequestType, contentType: string, response: Response, url: string, options: RequestInit) {
    return new RadHttpError({
      response,
      url,
      options,
      type
    }, `Response expected type ${type}, but Content-Type was ${contentType} which is not acceptable, for URL: ${url}`);
  }
}

/** The main radHttp instance that everything should use. */
export const radHttp = new RadHttp();